import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import BookDemoModal from '@/components/auth/BookDemoModal';

const ProductDropdown = (props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('Medica Clinic');
  const [showBookDemoModal, setShowBookDemoModal] = useState(false);
  const dropdownRef = useRef(null);
  const basePath = import.meta.env.VITE_BASE_PATH || '';

  const products = [
    {
      id: 'medica-clinic',
      name: t('menu_product_clinic'),
      description: t('menu_product_clinic_desc'),
      buttons: [
        { label: t('menu_product_button_register'), href: '/products/medica-clinic/register', primary: true },
        { label: t('menu_product_button_learn_more'), href: '/products/medica-clinic', primary: false }
      ],
      image: `${basePath}/scroll/Clinic.webp`
    },
    {
      id: 'medica-reception',
      name: t('menu_product_reception'),
      description: t('menu_product_reception_desc'),
      buttons: [
        { label: t('menu_product_button_register'), href: '/products/medica-reception/register', primary: true },
        { label: t('menu_product_button_learn_more'), href: '/products/medica-reception', primary: false }
      ],
      image: `${basePath}/scroll/Reception.webp`
    },
    {
      id: 'medica-patient-app',
      name: t('menu_product_patient_app'),
      description: t('menu_product_patient_app_desc'),
      buttons: [
        { label: t('menu_product_button_register'), href: '/products/medica-patient-app/register', primary: true },
        { label: t('menu_product_button_learn_more'), href: '/products/medica-patient-app', primary: false }
      ],
      image: `${basePath}/scroll/App.png`
    },
    {
      id: 'medica-pharmacy',
      name: t('menu_product_pharmacy'),
      description: t('menu_product_pharmacy_desc'),
      buttons: [
        { label: t('menu_product_button_register'), href: '/products/medica-pharmacy/register', primary: true },
        { label: t('menu_product_button_learn_more'), href: '/products/medica-pharmacy', primary: false }
      ],
      image: `${basePath}/scroll/pos.png`
    },
    {
      id: 'medica-telemedicine',
      name: t('menu_product_telemedicine'),
      description: t('menu_product_telemedicine_desc'),
      buttons: [
        { label: t('menu_product_button_register'), href: '/products/medica-telemedicine/register', primary: true },
        { label: t('menu_product_button_learn_more'), href: '/products/medica-telemedicine', primary: false }
      ],
      image: `${basePath}/products/telemedicine/hero-telemedicine.png`
    }
  ];

  const activeProduct = products.find(p => p.name === activeTab || p.id === activeTab) || products[0];

  // Add refs for timeouts
  const timeoutRef = useRef(null);
  const contentTimeoutRef = useRef(null);
  
  // Clean up the timeout if component unmounts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (contentTimeoutRef.current) {
        clearTimeout(contentTimeoutRef.current);
      }
    };
  }, []);

  // Smooth transition for content change
  const handleTabChange = (newTab) => {
    if (contentTimeoutRef.current) {
      clearTimeout(contentTimeoutRef.current);
    }
    setActiveTab(newTab);
  };

  const handleMouseEnter = () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    // Add a small delay before closing
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150); // 150ms delay gives time to move to dropdown
  };

  return (
    <div 
      className="relative " 
      ref={dropdownRef} 
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className={`flex items-center gap-1 ${
          isOpen || props.isActive ? 'text-gray-700 font-bold' : 'text-[#8C8C8C] font-bold'
        } hover:text-gray-700 relative`}
        type="button"
      >
        {t('menu_our_products')}
        <ChevronDown
          className={`ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          size={18}
          aria-label="Expand submenu icon"
        />
      </button>
      
      {/* Invisible bridge to prevent mouse leave when moving to dropdown */}
      <div className="absolute left-0 w-full h-4 top-full"></div>

      <div 
        className={`absolute top-full left-[250px] transform -translate-x-1/2 mt-4  rounded-lg shadow-xl z-50 w-[1200px] bg-white ring-1 ring-black/10  transition-all duration-300 origin-top ${
          isOpen 
            ? 'opacity-100 scale-100' 
            : 'opacity-0 scale-95 pointer-events-none'
        }`}
      >
        <div className="grid grid-cols-[260px_1fr] min-h-[400px] ">
            
            {/* Left Sidebar - Product List */}
            <div className="border-r py-6 border-black h-auto my-auto rounded-l-lg">
              {products.map((product) => (
                <Link
                  key={product.id}
                  to={product.buttons[1].href}
                  onClick={() => setIsOpen(false)}
                >
                  <div
                    onMouseEnter={() => handleTabChange(product.name)}
                    className={`w-full text-left px-6 py-3.5 transition-colors cursor-pointer ${
                      activeTab === product.name || activeTab === product.id
                        ? 'bg-[rgba(255,220,223,1)] font-semibold text-gray-900'
                        : 'text-gray-700 hover:bg-[rgba(255,220,223,0.7)]'
                    }`}
                  >
                    {product.name}
                  </div>
                </Link>
              ))}
            </div>

            {/* Right Content - Product Details */}
            <div className="p-8 py-12">
              <div className="grid grid-cols-[1fr_auto] gap-6 items-center transition-all duration-300 ease-in-out">
                
                {/* Content Section */}
                <div>
                  <h3 className="mb-4 text-2xl font-bold text-gray-900">
                    {activeProduct.name}
                  </h3>
                  <p className="mb-6 leading-relaxed text-gray-600">
                    {activeProduct.description}
                  </p>
                  
                  {/* Buttons */}
                  <div className="flex gap-3">
                    {activeProduct.buttons.map((button, index) => {
                      if (button.primary) {
                        // Register button - open demo modal
                        return (
                          <button
                            key={index}
                            onClick={() => {
                              setShowBookDemoModal(true);
                              setIsOpen(false);
                            }}
                            className="px-6 py-2 font-medium text-white transition-colors bg-gray-600 rounded-md cursor-pointer hover:bg-gray-700"
                          >
                            {button.label}
                          </button>
                        );
                      }
                      // Learn More button - clickable
                      return (
                        <Link
                          key={index}
                          to={button.href}
                          onClick={() => setIsOpen(false)}
                          className="px-6 py-2 font-medium text-gray-700 transition-colors bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          {button.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {/* Image/Logo Section */}
                <div className="w-[400px] h-[350px] bg-white rounded-2xl flex items-center justify-center">
                  <div className="relative w-full h-full">
                    {activeProduct.image && (
                      <img
                        src={activeProduct.image}
                        alt={activeProduct.name}
                        className="object-contain w-full h-full"
                      />
                    )}
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>

      <BookDemoModal
        open={showBookDemoModal}
        onClose={() => setShowBookDemoModal(false)}
        onSuccess={() => {
          // Optional: Handle success callback
        }}
      />
    </div>
  );
};

export default ProductDropdown;
